import {
  isToday,
  isThisWeek,
  isThisMonth,
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { Locale } from 'date-fns/locale';

import TaskInput from '@/apps/Checklist/components/TaskInput';
import TaskItem from '@/apps/Checklist/components/TaskItem';
import TaskProgress from '@/apps/Checklist/components/TaskProgress';
import { useChecklistStore, ViewMode } from '@/apps/Checklist/stores/useChecklistStore';
import { groupItemsByDate, calculateProgress } from '@/apps/Checklist/utils/date';
import LoadingSpinner from '@/common/components/LoadingSpinner';
import { useDateLocale } from '@/common/hooks/useDateLocale';
import { useAuthStore } from '@/common/stores/useAuthStore';

const getGroupLabel = (
  dateKey: string,
  viewMode: ViewMode,
  locale: Locale,
  t: (key: string) => string
): string => {
  const date = parseISO(dateKey);

  switch (viewMode) {
    case 'daily':
      return isToday(date) ? t('checklist.today') : format(date, 'MMMM d, yyyy', { locale });
    case 'weekly': {
      const weekStart = startOfWeek(date, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
      if (isThisWeek(date, { weekStartsOn: 1 })) {
        return t('checklist.thisWeek');
      }
      return `${format(weekStart, 'MMM d', { locale })} - ${format(weekEnd, 'MMM d, yyyy', { locale })}`;
    }
    case 'monthly':
      return isThisMonth(date) ? t('checklist.thisMonth') : format(date, 'MMMM yyyy', { locale });
    default:
      return format(date, 'MMMM d, yyyy', { locale });
  }
};

const isCurrentPeriod = (dateKey: string, viewMode: ViewMode): boolean => {
  const date = parseISO(dateKey);
  switch (viewMode) {
    case 'daily':
      return isToday(date);
    case 'weekly':
      return isThisWeek(date, { weekStartsOn: 1 });
    case 'monthly':
      return isThisMonth(date);
    default:
      return isToday(date);
  }
};

export default function Checklist({ widgetId }: { widgetId: string }) {
  const { t } = useTranslation();
  const { locale } = useDateLocale();
  const { user } = useAuthStore();
  const {
    items,
    itemsWithSubitems,
    loading,
    copyingItems,
    viewMode,
    fetchItems,
    addItem,
    toggleItem,
    deleteItem,
    updateItem,
    updatePriority,
    copyUnfinishedItems,
    loadUserPreferences,
  } = useChecklistStore();

  useEffect(() => {
    if (user) {
      loadUserPreferences(user.id);
    }
  }, [user, loadUserPreferences]);

  useEffect(() => {
    fetchItems(widgetId);
  }, [fetchItems, widgetId, viewMode]);

  const groupedItems = groupItemsByDate(itemsWithSubitems, viewMode);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-none py-3 px-1 bg-white border-b border-gray-100 mb-4">
        <div className="max-w-2xl mx-auto space-y-3">
          <TaskInput onSubmit={(content) => addItem(widgetId, content)} />
          <div className="flex items-center justify-between">
            <div />
            <TaskProgress percentage={calculateProgress(items, viewMode)} />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-1">
        <div className="max-w-2xl mx-auto space-y-6">
          {groupedItems.map(([date, dateItems]) => {
            const isCurrent = isCurrentPeriod(date, viewMode);
            const sortedItems = [...dateItems].sort((a, b) => b.priority - a.priority);
            const hasUnfinishedItems = dateItems.some((item) => !item.completed);

            return (
              <div
                key={date}
                className={`space-y-2 ${!isCurrent ? 'opacity-50 hover:opacity-90 transition-opacity' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-600 text-sm">
                    {getGroupLabel(date, viewMode, locale, t)}
                  </h3>

                  {!isCurrent && hasUnfinishedItems && (
                    <div className="relative group">
                      <button
                        className={`p-1.5 rounded-full text-gray-400 transition-all
                          ${
                            copyingItems
                              ? 'bg-gray-50 cursor-not-allowed'
                              : 'hover:text-gray-600 hover:bg-gray-50'
                          }`}
                        disabled={copyingItems}
                        onClick={() => !copyingItems && copyUnfinishedItems(widgetId, dateItems)}
                      >
                        {copyingItems ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              fill="currentColor"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            />
                          </svg>
                        )}
                      </button>
                      <div className="absolute right-0 top-full mt-1 hidden group-hover:block z-10">
                        <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          {t('checklist.copyUnfinishedToToday')}
                        </div>
                        <div className="absolute bottom-full right-3 border-4 border-transparent border-b-gray-900" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <AnimatePresence initial={false}>
                    {sortedItems.map((item) => (
                      <motion.div
                        key={item.id}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        initial={{ opacity: 0, y: 20 }}
                        transition={{
                          type: 'spring',
                          stiffness: 500,
                          damping: 30,
                          opacity: { duration: 0.2 },
                        }}
                        layout
                      >
                        <TaskItem
                          completed={item.completed}
                          content={item.content}
                          id={item.id}
                          isParent={item.parent_id === null}
                          priority={item.priority}
                          subitems={'subitems' in item ? item.subitems : undefined}
                          onAddSubitem={(parentId, content) => addItem(widgetId, content, parentId)}
                          onDelete={deleteItem}
                          onToggle={toggleItem}
                          onUpdate={updateItem}
                          onUpdatePriority={updatePriority}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {loading && <LoadingSpinner />}
    </div>
  );
}
